import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';

import GroupRiskMetricsTableAndPlot from '../group_risk_metrics/GroupRiskMetricsTableAndPlot';
import { buildGroupPresentation, emptyAttributeGroupMetrics } from '../../utils/riskOverviewPresentation';
import {
    isAssignedAttributeGroup,
    isWatchlistGroupingAttribute,
} from '../../utils/watchlistAttributePresentation';

const EMPTY_METRICS = Object.freeze({});

// Attribute choices as pill chips; exactly one is active.
const attributeChipSx = {
    height: 24,
    borderRadius: '999px',
    fontSize: '0.72rem',
    fontWeight: 650,
    textTransform: 'none',
    letterSpacing: 0,
    '& .MuiChip-label': { px: 1.1, textTransform: 'none' },
    transition: 'background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
};
// The app theme restyles every chip as a status badge, so both states set
// their colours explicitly.
const activeAttributeChipSx = {
    color: 'primary.contrastText',
    bgcolor: 'primary.main',
    borderColor: 'primary.main',
    boxShadow: (theme) => `0 2px 6px ${alpha(theme.palette.primary.main, 0.28)}`,
    '&:hover': { bgcolor: 'primary.dark' },
};
const inactiveAttributeChipSx = {
    color: 'text.secondary',
    bgcolor: 'background.paper',
    borderColor: 'divider',
    '&:hover': {
        color: 'text.primary',
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.45),
        bgcolor: (theme) => `${alpha(theme.palette.primary.main, 0.05)} !important`,
    },
};

const resultIndex = (payload) => new Map(
    (Array.isArray(payload?.attribute_results) ? payload.attribute_results : [])
        .filter((item) => String(item?.attribute_definition_id || '').trim())
        .map((item) => [String(item.attribute_definition_id), item]),
);

const rowsForResult = (result) => (
    (Array.isArray(result?.groups) ? result.groups : [])
        .filter((group) => isAssignedAttributeGroup(group?.group))
        .map((group) => ({
            category: String(group?.display_label || '').trim(),
            group: group?.group ?? null,
            ...(group?.metrics ?? {}),
        })).filter((row) => row.category)
);

const asGroupView = (payload, result, rows) => ({
    ...(payload ?? EMPTY_METRICS),
    success: result?.success ?? payload?.success,
    status: result?.status ?? payload?.status,
    message: result?.message ?? payload?.message,
    group_names: rows.map((row) => row.category),
    group_risks: rows,
});

export default function AttributeRiskMetricsTableAndPlot({
    actualMetrics,
    optimizedMetrics,
    actualLoading,
    optimizedLoading,
    optimalPortfolioLoaded,
    directExportEnabled,
    includeSelectedExport,
}) {
    const definitions = useMemo(() => (
        Array.isArray(actualMetrics?.attribute_definitions)
            ? actualMetrics.attribute_definitions.filter(isWatchlistGroupingAttribute)
            : []
    ), [actualMetrics?.attribute_definitions]);
    const [selectedDefinitionId, setSelectedDefinitionId] = useState(
        String(definitions[0]?.attribute_definition_id || ''),
    );

    useEffect(() => {
        const ids = definitions.map((item) => String(item.attribute_definition_id));
        setSelectedDefinitionId((current) => (
            ids.includes(current) ? current : (ids[0] || '')
        ));
    }, [definitions]);

    const actualById = useMemo(() => resultIndex(actualMetrics), [actualMetrics]);
    const optimizedById = useMemo(() => resultIndex(optimizedMetrics), [optimizedMetrics]);
    const hasOptimizedAttributeContract = optimizedMetrics?.contract
        === 'risk-overview-attribute-groups.v1';
    const fingerprintsMatch = definitions.length === 0 || !hasOptimizedAttributeContract || [
        'watchlist_revision_id',
        'attribute_definition_fingerprint',
        'attribute_content_fingerprint',
    ].every((field) => (
        Boolean(actualMetrics?.[field])
        && optimizedMetrics?.[field] === actualMetrics[field]
    ));
    const actualResult = actualById.get(selectedDefinitionId);
    const optimizedResult = fingerprintsMatch
        ? optimizedById.get(selectedDefinitionId)
        : null;
    const actualRows = useMemo(() => rowsForResult(actualResult), [actualResult]);
    const optimizedRows = useMemo(() => rowsForResult(optimizedResult), [optimizedResult]);
    const selectedDefinition = definitions.find(
        (item) => String(item.attribute_definition_id) === selectedDefinitionId,
    );
    const selectedAttributeName = String(selectedDefinition?.display_name || 'Attribute');
    const presentation = useMemo(() => {
        const canAlignGroups = hasOptimizedAttributeContract && fingerprintsMatch
            && !actualLoading && !optimizedLoading;
        const base = buildGroupPresentation(
            actualRows,
            optimizedRows,
            {
                includeSelectedExport,
                actualEmptyGroupMetrics: canAlignGroups
                    ? emptyAttributeGroupMetrics(actualMetrics, actualResult) : null,
                selectedEmptyGroupMetrics: canAlignGroups
                    ? emptyAttributeGroupMetrics(optimizedMetrics, optimizedResult) : null,
            },
        );
        return {
            ...base,
            exportRows: base.exportRows.map(({ Group, ...row }) => ({
                Attribute: selectedAttributeName,
                Category: Group,
                ...row,
            })),
        };
    }, [actualRows, includeSelectedExport, optimizedRows, selectedAttributeName,
        actualLoading, optimizedLoading, actualMetrics, optimizedMetrics,
        actualResult, optimizedResult, fingerprintsMatch, hasOptimizedAttributeContract]);

    const tabs = definitions.length > 0 ? (
        <Box
            role="radiogroup"
            aria-label="Risk attributes"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', minWidth: 0 }}
        >
            {definitions.map((definition) => {
                const id = String(definition.attribute_definition_id);
                const active = id === selectedDefinitionId;
                return (
                    <Chip
                        key={id}
                        role="radio"
                        aria-checked={active}
                        label={definition.display_name}
                        size="small"
                        clickable
                        color={active ? 'primary' : 'default'}
                        variant={active ? 'filled' : 'outlined'}
                        onClick={() => setSelectedDefinitionId(id)}
                        sx={[
                            attributeChipSx,
                            active ? activeAttributeChipSx : inactiveAttributeChipSx,
                        ]}
                    />
                );
            })}
        </Box>
    ) : null;

    return (
        <>
            <GroupRiskMetricsTableAndPlot
                actGroupRiskMetrics={asGroupView(
                    actualMetrics,
                    actualResult,
                    [...presentation.actualByGroup.values()],
                )}
                optimizedGroupRiskMetrics={asGroupView(
                    optimizedMetrics,
                    optimizedResult,
                    [...presentation.selectedByGroup.values()],
                )}
                groupRisksLoading={actualLoading}
                optimizedGroupRiskMetricsLoading={optimizedLoading}
                optimalPortfolioLoaded={optimalPortfolioLoaded}
                presentation={presentation}
                directExportEnabled={directExportEnabled}
                title="By Attribute"
                groupLabel="Category"
                groupLabelPlural="Categories"
                headerTabs={tabs}
                exportFileName={`by_attribute_${String(selectedDefinition?.display_name || 'groups')
                    .replace(/[^a-z0-9]+/gi, '_')
                    .replace(/^_+|_+$/g, '')
                    .toLowerCase() || 'groups'}.xlsx`}
                exportSheetName="ByAttribute"
            />
        </>
    );
}

AttributeRiskMetricsTableAndPlot.propTypes = {
    actualMetrics: PropTypes.object,
    optimizedMetrics: PropTypes.object,
    actualLoading: PropTypes.bool.isRequired,
    optimizedLoading: PropTypes.bool.isRequired,
    optimalPortfolioLoaded: PropTypes.bool.isRequired,
    directExportEnabled: PropTypes.bool.isRequired,
    includeSelectedExport: PropTypes.bool.isRequired,
};
